import React, { useEffect, useState } from 'react';
import RecaptchaControl from './RecaptchaControl';
import { useHistory } from 'react-router-dom';
import { search } from '../../lib/chipsApi';
import { useSelector } from 'react-redux';
import { AppState } from '../../lib/store';
import { useQuery } from '@apollo/client';
import { gqlTypes } from '../../types/gqlTypes';
import { userChipResellerQuery } from '../../lib/utils';
import { chipContactInfoForVerifiedRescueQuery } from './ChipContactInfoForVerifiedRescue.graphql';
import { SearchState } from '../../lib/SearchState';

export interface SearchVerificationProps {
  query: string;
}
/*
  Logged in verified rescue users: can see full contact info for chip
  All other users: can see only redacted contact info
*/
export default function SearchVerification({ query }: SearchVerificationProps) {
  const [error, setError] = useState(false);
  const [state, setState] = useState<SearchState>();
  const history = useHistory();

  const isLoggedIn = useSelector((state: AppState) => !!state.session);

  const { data: currentUserReseller, loading: userResellerLoading } = useQuery<gqlTypes.userChipReseller>(
    userChipResellerQuery,
    {
      skip: !isLoggedIn,
    },
  );

  const currentUserIsVerifiedRescue = !!currentUserReseller?.currentUser.chipReseller?.verified;

  const { data: chipContactInfo, loading: chipContactInfoLoading } =
    useQuery<gqlTypes.chipContactInfoForVerifiedRescue>(chipContactInfoForVerifiedRescueQuery, {
      variables: {
        id: query,
      },
      skip: !isLoggedIn || !currentUserIsVerifiedRescue || !state,
    });

  useEffect(() => {
    if (state && currentUserIsVerifiedRescue && chipContactInfo) {
      const contactHistory = state?.response.result?.contactHistory ?? null;
      let contactHistoryEntry;
      if (Array.isArray(contactHistory) && contactHistory.length > 0) {
        const createdAt = state?.response.result?.contactHistory[0].createdAt;
        const id = state?.response.result?.contactHistory[0].id;
        contactHistoryEntry = [
          { createdAt, id, ...chipContactInfo.chipContactInfoForVerifiedRescue?.latestContactHistoryEntry },
        ];
      }

      const result = { ...state?.response.result, contactHistory: contactHistoryEntry };

      history.push({
        pathname: '/verifiedRescueSearch',
        state: { ...state, response: { result } },
      });
    } else if (state && !userResellerLoading && !chipContactInfoLoading) {
      history.push({ pathname: '/search', state });
    }
  }, [chipContactInfo, chipContactInfoLoading, currentUserIsVerifiedRescue, history, state, userResellerLoading]);

  const gotToken = async (token: string) => {
    // Issue search using token
    try {
      const response = await search(query, token);
      setState({
        isSearchState: true,
        query,
        response,
      });
    } catch (err) {
      console.error(err);
      setError(true);
    }
  };

  let body: JSX.Element;
  if (error) {
    body = (
      <>
        <h4>Sorry, we were unable to perform your search.</h4>
      </>
    );
  } else {
    body = (
      <>
        <h4>Please check the box below to proceed.</h4>
        <RecaptchaControl onToken={gotToken} />
      </>
    );
  }

  return <div className="single-column single-column--centered">{body}</div>;
}
